import { ChangeEvent, useEffect, useState } from 'react';
import classNames from 'classnames';
import Modal from '../../../components/Modal';

interface ClinicalTrialInquiryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trialName: string;
}

type ContactType = 'phone' | 'email';

const ClinicalTrialInquiryModal = ({ open, onOpenChange, trialName }: ClinicalTrialInquiryModalProps) => {
  const [contactType, setContactType] = useState<ContactType>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [applyThisTrial, setApplyThisTrial] = useState(true);
  const [receiveMatchingNews, setReceiveMatchingNews] = useState(false);
  const [consentPersonalInfo, setConsentPersonalInfo] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setIsSubmitted(false);
  }, [open]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    if (formatted.replace(/-/g, '').length <= 11) {
      setPhoneNumber(formatted);
    }
  };

  const isPhoneValid = phoneNumber.replace(/-/g, '').length >= 10;
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isContactValid = contactType === 'phone' ? isPhoneValid : isEmailValid;
  const hasSelectedOption = applyThisTrial || receiveMatchingNews;
  const isFormValid = isContactValid && consentPersonalInfo && hasSelectedOption;

  const handleSubmit = async () => {
    if (!isFormValid) return;

    setIsSubmitting(true);
    await new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 700);
    });
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const resetForm = () => {
    setContactType('phone');
    setPhoneNumber('');
    setEmail('');
    setApplyThisTrial(true);
    setReceiveMatchingNews(false);
    setConsentPersonalInfo(false);
    setConsentMarketing(false);
    setIsSubmitted(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      resetForm();
    }, 200);
  };

  return (
    <Modal
      open={open}
      onClickBackdrop={handleClose}
      containerCls='flex items-center justify-center'
      className='bg-white rounded-2xl overflow-y-auto'
      boxStyle={{
        margin: 'auto',
        width: '90vw',
        maxWidth: '24rem',
        height: 'auto',
        maxHeight: '85vh',
      }}
    >
      {!isSubmitted ? (
        <div className='flex flex-col gap-4'>
          <div className='text-center space-y-2'>
            <div className='text-lg font-semibold'>임상시험 신청</div>
            <div className='text-sm text-muted-foreground'>원하시는 항목을 선택하고 연락처를 남겨주세요.</div>
          </div>

          <div className='space-y-2'>
            <div className='text-sm font-medium'>신청 항목 <span className='text-error'>*</span></div>
            <div className='space-y-2'>
              <button
                type='button'
                onClick={() => setApplyThisTrial(!applyThisTrial)}
                className={classNames(
                  'w-full flex items-start gap-3 p-3 rounded-lg border-2 transition-colors text-left',
                  applyThisTrial ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/50'
                )}
              >
                <input
                  type='checkbox'
                  checked={applyThisTrial}
                  readOnly
                  className='checkbox checkbox-primary mt-1'
                />
                <div>
                  <p className={classNames('font-medium text-sm', applyThisTrial ? 'text-primary' : 'text-foreground')}>
                    {trialName} 임상시험 참여 신청
                  </p>
                  <p className='text-xs text-muted-foreground'>이 임상시험에 대한 상담을 받습니다</p>
                </div>
              </button>
              <button
                type='button'
                onClick={() => {
                  const newValue = !receiveMatchingNews;
                  setReceiveMatchingNews(newValue);
                  setConsentMarketing(newValue);
                }}
                className={classNames(
                  'w-full flex items-start gap-3 p-3 rounded-lg border-2 transition-colors text-left',
                  receiveMatchingNews ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/50'
                )}
              >
                <input
                  type='checkbox'
                  checked={receiveMatchingNews}
                  readOnly
                  className='checkbox checkbox-primary mt-1'
                />
                <div>
                  <p className={classNames('font-medium text-sm', receiveMatchingNews ? 'text-primary' : 'text-foreground')}>
                    나에게 맞는 임상시험 소식 받기
                  </p>
                  <p className='text-xs text-muted-foreground'>새로운 임상시험 정보를 받습니다</p>
                </div>
              </button>
            </div>
          </div>

          <div className='space-y-2'>
            <div className='text-sm font-medium'>연락 방법 <span className='text-error'>*</span></div>
            <div className='grid grid-cols-2 gap-2'>
              <button
                type='button'
                onClick={() => setContactType('phone')}
                className={classNames(
                  'flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-colors',
                  contactType === 'phone'
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                )}
              >
                전화번호
              </button>
              <button
                type='button'
                onClick={() => setContactType('email')}
                className={classNames(
                  'flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-colors',
                  contactType === 'email'
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                )}
              >
                이메일
              </button>
            </div>
          </div>

          {contactType === 'phone' ? (
            <input
              type='tel'
              placeholder='010-0000-0000'
              value={phoneNumber}
              onChange={handlePhoneChange}
              className='input input-bordered w-full h-12 text-base'
            />
          ) : (
            <input
              type='email'
              placeholder='example@email.com'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className='input input-bordered w-full h-12 text-base'
            />
          )}

          <div className='space-y-3 pt-2'>
            <label className='flex items-start gap-3'>
              <input
                type='checkbox'
                className='checkbox checkbox-primary mt-1'
                checked={consentPersonalInfo}
                onChange={(e) => setConsentPersonalInfo(e.target.checked)}
              />
              <span className='text-sm text-muted-foreground leading-relaxed'>
                <span className='text-error'>[필수]</span> 개인정보 수집 및 이용에 동의합니다.
                <br />
                <span className='text-xs'>(연락처, 임상시험 관심 분야)</span>
              </span>
            </label>

            <label className='flex items-start gap-3'>
              <input
                type='checkbox'
                className='checkbox checkbox-primary mt-1'
                checked={consentMarketing}
                onChange={(e) => setConsentMarketing(e.target.checked)}
              />
              <span className='text-sm text-muted-foreground leading-relaxed'>
                <span className='text-muted-foreground'>[선택]</span> 마케팅 정보 수신에 동의합니다.
                <br />
                <span className='text-xs'>(다른 임상시험 정보 안내)</span>
              </span>
            </label>
          </div>

          <button
            type='button'
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className='btn btn-primary w-full h-12 text-base font-semibold mt-2'
          >
            {isSubmitting ? '신청 중...' : '신청하기'}
          </button>

          <p className='text-xs text-center text-muted-foreground'>신청하신 연락처로 담당자가 연락드립니다.</p>
        </div>
      ) : (
        <div className='py-6 text-center space-y-4'>
          <div className='space-y-2'>
            <h3 className='text-lg font-bold text-foreground'>신청이 완료되었습니다!</h3>
            <p className='text-sm text-muted-foreground'>입력하신 연락처로 임상시험 담당자가 연락드릴 예정입니다.</p>
          </div>
          <button type='button' onClick={handleClose} className='btn btn-outline mt-4'>
            확인
          </button>
        </div>
      )}
    </Modal>
  );
};

export default ClinicalTrialInquiryModal;
